import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { EffectiveRole, normalizeRole } from "@/lib/team-roles";

export type Workspace = {
  id: string;
  name: string;
  role: EffectiveRole;
  isOwner: boolean;
};

type WorkspaceContextValue = {
  workspaces: Workspace[];
  activeWorkspace: Workspace | null;
  setActiveWorkspaceId: (id: string) => void;
  loading: boolean;
  refresh: () => Promise<void>;
};

const STORAGE_KEY = "veylo.active-workspace";

const WorkspaceContext = createContext<WorkspaceContextValue>({
  workspaces: [],
  activeWorkspace: null,
  setActiveWorkspaceId: () => {},
  loading: true,
  refresh: async () => {},
});

export const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [activeId, setActiveId] = useState<string | null>(
    () => localStorage.getItem(STORAGE_KEY),
  );
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const uid = session?.user?.id;
    if (!uid) {
      setWorkspaces([]);
      setLoading(false);
      return;
    }

    const [{ data: owned }, { data: memberships }] = await Promise.all([
      supabase.from("teams").select("id, name").eq("owner_id", uid),
      supabase.from("team_members").select("role, team_id, teams(id, name)").eq("user_id", uid),
    ]);

    const map = new Map<string, Workspace>();
    (owned ?? []).forEach((t) =>
      map.set(t.id, { id: t.id, name: t.name, role: "owner", isOwner: true }),
    );
    (memberships ?? []).forEach((m: any) => {
      const team = m.teams;
      if (!team || map.has(team.id)) return;
      map.set(team.id, {
        id: team.id,
        name: team.name,
        role: normalizeRole(m.role),
        isOwner: false,
      });
    });

    const list = Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    setWorkspaces(list);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(() => { setTimeout(() => load(), 0); });
    return () => subscription.unsubscribe();
  }, [load]);

  useEffect(() => {
    if (!workspaces.length) return;
    if (!activeId || !workspaces.some((w) => w.id === activeId)) {
      setActiveId(workspaces[0].id);
    }
  }, [workspaces, activeId]);

  const setActiveWorkspaceId = useCallback((id: string) => {
    setActiveId(id);
    localStorage.setItem(STORAGE_KEY, id);
  }, []);

  const value = useMemo<WorkspaceContextValue>(() => ({
    workspaces,
    activeWorkspace: workspaces.find((w) => w.id === activeId) ?? null,
    setActiveWorkspaceId,
    loading,
    refresh: load,
  }), [workspaces, activeId, setActiveWorkspaceId, loading, load]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
};

export const useWorkspace = () => useContext(WorkspaceContext);

export default WorkspaceProvider;
